"use client";

import { ListBox, DefaultItem } from "@ui/components/core/ListBox";
import { clsx } from "clsx";
import { memo } from "react";
import { Button, SelectProps, SelectValue, Select as RACSelect } from "react-aria-components";

export const Select = memo(function Select<T extends { id: string; textValue: string }>({
  items,
  virtualize,
  listBoxClassName,
  listBoxStyle,
  scrollTo,
  hideSelectedLabelOnMobile = false,
  hideSelectedImageOnMobile = false,
  ...props
}: Omit<SelectProps<T>, "children"> & {
  defaultValue?: string;
  listBoxClassName?: string;
  listBoxStyle?: React.CSSProperties;
  hideSelectedLabelOnMobile?: boolean;
  hideSelectedImageOnMobile?: boolean;
} & Pick<React.ComponentProps<typeof ListBox<T>>, "virtualize" | "items" | "scrollTo">) {
  return (
    <RACSelect {...props}>
      {({ isOpen }) => (
        <>
          <Button
            className={({ isFocusVisible }) =>
              clsx(
                "tw-w-full tw-text-sm/sm tw-font-medium tw-text-black tw-flex tw-items-center tw-gap-2 tw-h-9 tw-rounded-xl tw-pl-3 tw-pr-2",
                isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
              )
            }
          >
            {({ isHovered }) => (
              <>
                <SelectValue<T> className='tw-min-w-0'>
                  {({ selectedItems }) =>
                    selectedItems[0] ? (
                      <DefaultItem
                        item={selectedItems[0]}
                        showSubLabel={false}
                        labelClassName={hideSelectedLabelOnMobile ? "max-sm:tw-hidden" : undefined}
                        imageClassName={hideSelectedImageOnMobile ? "max-sm:tw-hidden" : undefined}
                      />
                    ) : null
                  }
                </SelectValue>
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  className={clsx(
                    "tw-size-4 tw-flex-none tw-transition",
                    isOpen || isHovered ? "tw-text-black" : "tw-text-gray-400",
                  )}
                  aria-hidden
                >
                  <path
                    d='M8 10L11.2929 13.2929C11.6834 13.6834 12.3166 13.6834 12.7071 13.2929L16 10'
                    stroke='currentColor'
                    strokeWidth='2.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </>
            )}
          </Button>
          <ListBox
            items={items ?? []}
            offset={0}
            crossOffset={-4}
            virtualize={virtualize}
            className={listBoxClassName}
            style={listBoxStyle}
            scrollTo={scrollTo}
          />
        </>
      )}
    </RACSelect>
  );
});
